// ** Modules
import React from 'react';

// ** UI
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import ClearIcon from '@mui/icons-material/Clear';

// ** Components
import { Icon } from '@/components/common/icons';

// ** Types
import type { SearchSectionInputProps } from './types';

const SearchSectionInput = ({
	id,
	value,
	placeholder,
	onChange,
	onSubmit,
	disabled = false,
	sx
}: SearchSectionInputProps) => {
	const handleSubmit = React.useCallback(() => {
		if (value !== undefined) {
			onSubmit?.(value);
		}
	}, [onSubmit, value]);

	return (
		<Box
			sx={[
				{
					width: '100%',
					minWidth: 0,
					overflow: 'hidden',
					height: 48,
					borderBottom: '1px solid',
					borderColor: 'grey.400',
					transition: 'border-color 180ms ease',
					':focus-within': {
						borderColor: 'common.black'
					}
				},
				...(Array.isArray(sx) ? sx : sx ? [sx] : [])
			]}
		>
			<TextField
				id={id}
				variant="standard"
				fullWidth
				disabled={disabled}
				value={value ?? ''}
				onChange={(event) => onChange?.(event.target.value)}
				placeholder={placeholder}
				onKeyDown={(event) => {
					if (event.key === 'Enter') {
						event.preventDefault();
						handleSubmit();
					}
				}}
				slotProps={{
					htmlInput: { 'aria-label': placeholder },
					input: {
						disableUnderline: true,
						endAdornment: (
							<InputAdornment position="end" sx={{ gap: 3 }}>
								{value && (
									<IconButton
										size="small"
										onClick={() => onChange?.('')}
										aria-label="Suche löschen"
										sx={{
											p: 0,
											color: 'grey.500',
											'&:hover': {
												backgroundColor: 'transparent',
												color: 'common.black'
											},
											transition: 'color 150ms ease'
										}}
									>
										<ClearIcon sx={{ fontSize: 16 }} />
									</IconButton>
								)}
								<IconButton
									size="small"
									onClick={handleSubmit}
									aria-label={`${placeholder} ausführen`}
									sx={{
										p: 0,
										pr: 0.5,
										color: 'common.black',
										'&:hover': { backgroundColor: 'transparent' }
									}}
								>
									<Icon name="search" size={16} style={{ flexShrink: 0 }} />
								</IconButton>
							</InputAdornment>
						)
					}
				}}
				sx={{
					height: '100%',
					'& .MuiInputBase-root': {
						height: '100%',
						minWidth: 0,
						transition: 'color 180ms ease'
					},
					'& .MuiInputBase-input': {
						height: '100%',
						minWidth: 0,
						pl: { xs: 0, lg: 4 },
						pr: 0,
						py: 0,
						color: 'common.black',
						fontFamily: 'inherit',
						fontSize: 18,
						fontWeight: 400,
						lineHeight: '24px',
						letterSpacing: '-0.05px',
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						transition: 'color 180ms ease'
					},
					'& .MuiInputBase-input::placeholder': {
						color: 'common.black',
						opacity: 1,
						transition: 'opacity 180ms ease'
					},
					'& .MuiInputBase-input:focus::placeholder': {
						opacity: 0
					}
				}}
			/>
		</Box>
	);
};

export default SearchSectionInput;
